import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from 'recharts';
import {
  Users,
  Calendar,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const AdminCharts = () => {
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    registrations: [],
    appointments: []
  });

  useEffect(() => {
    loadChartData();
  }, [period]);

  const loadChartData = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getChartData(period);
      setChartData(response.data);
    } catch (error) {
      console.error('Error loading chart data:', error);
      toast.error('Erreur lors du chargement des graphiques');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (period === 'year') {
        return date.toLocaleDateString('fr-FR', { month: 'short' });
      }
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2].map(i => (
          <Card key={i} className="border-0 shadow-sm">
            <CardContent className="flex items-center justify-center h-[300px]">
              <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-end">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="chart-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">7 derniers jours</SelectItem>
            <SelectItem value="month">30 derniers jours</SelectItem>
            <SelectItem value="year">12 derniers mois</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registrations Chart */}
        <Card className="border-0 shadow-sm" data-testid="registrations-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" />
              Inscriptions
            </CardTitle>
            <CardDescription>
              Évolution des nouvelles inscriptions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.registrations?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chartData.registrations}>
                  <defs>
                    <linearGradient id="colorPatients" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorPros" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E53935" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#E53935" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip
                    formatter={(value, name) => {
                      const labels = {
                        patients: 'Patients',
                        professionals: 'Professionnels',
                        facilities: 'Établissements',
                        total: 'Total'
                      };
                      return [value, labels[name] || name];
                    }}
                    labelFormatter={formatDate}
                  />
                  <Legend 
                    formatter={(value) => {
                      const labels = {
                        patients: 'Patients',
                        professionals: 'Professionnels',
                        total: 'Total'
                      };
                      return labels[value] || value;
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="patients" 
                    stroke="#3B82F6" 
                    fillOpacity={1} 
                    fill="url(#colorPatients)" 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="professionals" 
                    stroke="#E53935" 
                    fillOpacity={1} 
                    fill="url(#colorPros)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune donnée pour cette période</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appointments Chart */}
        <Card className="border-0 shadow-sm" data-testid="appointments-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-500" />
              Rendez-vous
            </CardTitle>
            <CardDescription>
              Statistiques des rendez-vous
            </CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.appointments?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData.appointments}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip
                    formatter={(value, name) => {
                      const labels = {
                        completed: 'Terminés',
                        pending: 'En attente',
                        cancelled: 'Annulés',
                        total: 'Total'
                      };
                      return [value, labels[name] || name];
                    }}
                    labelFormatter={formatDate}
                  />
                  <Legend 
                    formatter={(value) => {
                      const labels = {
                        completed: 'Terminés',
                        pending: 'En attente',
                        cancelled: 'Annulés'
                      };
                      return labels[value] || value;
                    }}
                  />
                  <Bar dataKey="completed" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pending" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cancelled" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune donnée pour cette période</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCharts;
